import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import * as R from 'ramda';

import { AnalyticsService, EVENTS, logBreadcrumb } from '@/services';
import { SnackbarUtils } from '@/utils';

import { memberManagementApi } from '../member-management-api';
import {
  onboardingApiPostgrest,
  selectDisclaimerDesignationOptions,
} from '../onboarding-api';
import { RootState } from '../store';
import { uiActions } from '../ui';
import { selectIsB2CUser, userApiPostgrest } from '../user-api';
import {
  selectOnboardingActiveStep,
  selectOnboardingForm,
  selectOnboardingStepInputs,
} from './onboarding.selectors';
import {
  OnboardingForm,
  OnboardingState,
  STEP_IDS,
  StepId,
} from './onboarding.types';
import {
  getNextStep,
  getPrevStep,
  getSteps,
  stepsConfig,
} from './onboarding.utils';

export const ONBOARDING_SLICE_KEY = 'onboarding';

const initialState: OnboardingState = {
  isHandlingStepTransition: false,
  form: {
    isTeamUse: null,
    userCountry: null,
    designations: null,
    name: null,
    jobDetails: null,
  },
  activeStep: STEP_IDS.introduction,
  stepInputs: R.map(
    stepConfig => ({ value: stepConfig.initialInput }),
    stepsConfig,
  ),
};

const goToPrevStep = createAsyncThunk<
  { updatedForm: OnboardingForm; updatedActiveStep: StepId } | void,
  void,
  { state: RootState }
>(`${ONBOARDING_SLICE_KEY}/goToPrevStep`, async (_, { getState }) => {
  const isB2CUser = selectIsB2CUser(getState());
  const disclaimerDesignationOptions =
    selectDisclaimerDesignationOptions(getState());

  const activeStep = selectOnboardingActiveStep(getState());
  const onboardingForm = selectOnboardingForm(getState());

  const steps = getSteps(onboardingForm, {
    isB2CUser,
    disclaimerDesignationOptions,
  });
  const prevStep = getPrevStep({ steps, activeStep });

  logBreadcrumb({
    message: 'Onboarding goToPrevStep',
    data: {
      activeStep,
      prevStep,
    },
  });

  return { updatedForm: onboardingForm, updatedActiveStep: prevStep };
});

const goToNextStep = createAsyncThunk<
  { updatedForm: OnboardingForm; updatedActiveStep: StepId } | void,
  void,
  { state: RootState }
>(`${ONBOARDING_SLICE_KEY}/goToNextStep`, async (_, { getState, dispatch }) => {
  const isB2CUser = selectIsB2CUser(getState());
  const activeStep = selectOnboardingActiveStep(getState());
  const stepInputs = selectOnboardingStepInputs(getState());
  const onboardingForm = selectOnboardingForm(getState());
  const disclaimerDesignationOptions =
    selectDisclaimerDesignationOptions(getState());

  const activeStepConfig = stepsConfig[activeStep];
  const { value: activeStepInput } = stepInputs[activeStep];
  const isStepInputValid = activeStepConfig.isStepInputValid(activeStepInput);

  if (!isStepInputValid) {
    dispatch(onboardingActions.setInputError({ step: activeStep }));
    throw new Error();
  }

  const updatedForm = activeStepConfig.handleNext(
    onboardingForm,
    activeStepInput,
  );
  const steps = getSteps(updatedForm, {
    isB2CUser,
    disclaimerDesignationOptions,
  });

  const nextStep = getNextStep({ steps, activeStep });
  const isFinalStepOfDesignations = nextStep === STEP_IDS.name;
  const isFinalStepOfOnboarding = steps[steps.length - 1] === activeStep;

  logBreadcrumb({
    message: 'Onboarding goToNextStep',
    data: {
      activeStep,
      activeStepInput,
      updatedForm: JSON.stringify(updatedForm),
      steps,
      nextStep,
    },
  });

  if (isFinalStepOfDesignations) {
    const { userCountry, designations: userDesignations } = updatedForm;
    const result = await dispatch(
      userApiPostgrest.endpoints.updateUserDesignations.initiate({
        userDesignations: userDesignations!,
        userCountry: userCountry!,
      }),
    );

    if (result.error) {
      SnackbarUtils.error(
        'Failed to update designations, please try again, or contact support.',
      );
      throw new Error();
    }
  }

  if (activeStep === STEP_IDS.name) {
    const result = await dispatch(
      userApiPostgrest.endpoints.updateUserProfile.initiate({
        firstName: updatedForm.name?.first.trim(),
        lastName: updatedForm.name?.last.trim(),
        event: EVENTS.ONBOARDING_EVENTS.UPDATE_PROFILE,
      }),
    );

    if (result.error) {
      SnackbarUtils.error(
        'Failed to update profile, please try again, or contact support.',
      );
      throw new Error();
    }
  }

  if (activeStep === STEP_IDS.b2cJobDetails) {
    dispatch(
      userApiPostgrest.endpoints.updateUserProfile.initiate({
        ...updatedForm.jobDetails,
        event: EVENTS.ONBOARDING_EVENTS.UPDATE_JOB_DETAILS,
      }),
    );
  }

  if (isFinalStepOfOnboarding) {
    if (updatedForm.isTeamUse) {
      try {
        await dispatch(
          memberManagementApi.endpoints.createNewTeam.initiate({
            companyName: updatedForm.jobDetails?.companyName || '',
          }),
        ).unwrap();
      } catch (error) {
        SnackbarUtils.error(
          'Failed to create team, please try again, or contact support.',
        );
        throw error;
      }
    }

    const result = await dispatch(
      onboardingApiPostgrest.endpoints.completeOnboarding.initiate(),
    );

    if (result.error) {
      SnackbarUtils.error(
        'Failed to complete onboarding, please try again, or contact support.',
      );

      throw new Error();
    }

    AnalyticsService.logEvent(EVENTS.ONBOARDING_EVENTS.FINISH);
    dispatch(uiActions.closeActiveModal());
  } else {
    AnalyticsService.logEvent(EVENTS.ONBOARDING_EVENTS.STEP, {
      step_name: nextStep,
    });

    return { updatedForm, updatedActiveStep: nextStep };
  }
});

export const onboardingSlice = createSlice({
  name: ONBOARDING_SLICE_KEY,
  initialState,
  reducers: {
    updateStepInputs: (
      state,
      { payload }: { payload: { step: keyof typeof STEP_IDS; input: any } },
    ) => {
      const { step, input } = payload;
      const stepConfig = stepsConfig[step];
      const isStepInputValid = stepConfig.isStepInputValid(input);

      state.stepInputs[step].value = input;
      if (isStepInputValid) {
        state.stepInputs[step].error = undefined;
      }
    },
    setInputError: (state, { payload }: { payload: { step: StepId } }) => {
      const { step } = payload;
      state.stepInputs[step].error = true;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      isAnyOf(goToNextStep.pending, goToPrevStep.pending),
      state => {
        state.isHandlingStepTransition = true;
      },
    );
    builder.addMatcher(
      isAnyOf(goToNextStep.rejected, goToPrevStep.rejected),
      state => {
        state.isHandlingStepTransition = false;
      },
    );
    builder.addMatcher(
      isAnyOf(goToNextStep.fulfilled, goToPrevStep.fulfilled),
      (state, { payload }) => {
        if (payload) {
          const { updatedForm, updatedActiveStep } = payload;
          state.form = updatedForm;
          state.activeStep = updatedActiveStep;
        }
        state.isHandlingStepTransition = false;
      },
    );
  },
});

export const onboardingThunks = {
  goToNextStep,
  goToPrevStep,
};

export const { reducer: onboardingReducer, actions: onboardingActions } =
  onboardingSlice;
